import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { InstagramConnectResult, InstagramStatus } from "@school-sales-ai/shared";
import { api } from "@/lib/api";

const STATUS_KEY = ["instagram-status"] as const;

/** The client's Instagram connection, entitlement and server switch. Never 403s. */
export function useInstagramStatus() {
  return useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api.get<InstagramStatus>("/crm/instagram/status"),
    staleTime: 30_000,
  });
}

function onConnected(
  queryClient: ReturnType<typeof useQueryClient>,
  result: InstagramConnectResult,
): void {
  if (result.status !== "connected") return;
  queryClient.setQueryData(STATUS_KEY, result.connection);
  toast.success(
    result.connection.username
      ? `Instagram conectado: @${result.connection.username}`
      : "Instagram conectado",
  );
}

/** Completes Facebook Login with the authorization `code`. */
export function useConnectInstagram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<InstagramConnectResult>("/crm/instagram/connect", { code }),
    onSuccess: (result) => onConnected(queryClient, result),
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo conectar Instagram");
    },
  });
}

/** Answers the "which Page?" choice the login left pending. */
export function useChooseInstagramPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { selectionId: string; pageId: string }) =>
      api.post<InstagramConnectResult>("/crm/instagram/connect/page", input),
    onSuccess: (result) => onConnected(queryClient, result),
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo conectar Instagram");
    },
  });
}

export function useDisconnectInstagram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<InstagramStatus>("/crm/instagram/connection"),
    onSuccess: (status) => {
      queryClient.setQueryData(STATUS_KEY, status);
      toast.success("Instagram desconectado");
    },
    onError: (err: Error) => {
      toast.error(err.message || "No se pudo desconectar Instagram");
    },
  });
}
